import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Clock, Calendar as CalendarIcon, Check, Info, Repeat, Settings } from 'lucide-react';
import { format, addDays, parse, addWeeks } from 'date-fns';
import { 
  getCalendarEvents, 
  findAvailableTimeSlots, 
  addWorkoutToCalendar,
  type TimeSlot,
  type CalendarEvent 
} from '../../../utils/calendar';
import { useSchedulerStore } from '../../../store/schedulerStore';
import { SchedulerSettings } from './SchedulerSettings';
import { WorkoutCalendar } from './WorkoutCalendar';

interface WorkoutSchedulerProps {
  workout: {
    id?: string;
    name: string;
    duration: number;
  };
  onClose: () => void;
  onScheduled: () => void;
  isRescheduling?: boolean;
}

export function WorkoutScheduler({ workout, onClose, onScheduled, isRescheduling = false }: WorkoutSchedulerProps) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recurrence, setRecurrence] = useState<'none' | 'weekly' | 'biweekly'>('none');
  const [recurrenceWeeks, setRecurrenceWeeks] = useState(4);
  const [showSettings, setShowSettings] = useState(false);
  const { settings } = useSchedulerStore();
  const [existingWorkout, setExistingWorkout] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      
      const calendarEvents = await getCalendarEvents(startOfDay, endOfDay);
      setEvents(calendarEvents);

      // Find existing workout if rescheduling
      if (isRescheduling && workout.id) {
        const existing = calendarEvents.find(event => 
          event.type === 'workout' && event.id === workout.id
        );
        setExistingWorkout(existing || null);
      }
      
      const slots = findAvailableTimeSlots(
        calendarEvents,
        selectedDate,
        workout.duration,
        settings
      );

      // Sort slots by proximity to preferred times
      const sortedSlots = slots.sort((a, b) => {
        const aTime = format(a.start, 'HH:mm');
        const bTime = format(b.start, 'HH:mm');
        const aPreferred = settings.preferredTimes.includes(aTime);
        const bPreferred = settings.preferredTimes.includes(bTime);
        if (aPreferred && !bPreferred) return -1;
        if (!aPreferred && bPreferred) return 1;
        return 0;
      });

      setAvailableSlots(sortedSlots);
    };

    fetchEvents();
  }, [selectedDate, workout.duration, settings, isRescheduling, workout.id]);

  const handleSchedule = async () => {
    if (!selectedSlot) return;
    
    setIsScheduling(true);

    try {
      // If rescheduling, handle the existing workout
      if (isRescheduling && existingWorkout) {
        // Here you would typically delete the existing workout
        // For now we'll just log it
        console.log('Removing existing workout:', existingWorkout);
      }

      // Schedule the workout
      const success = await addWorkoutToCalendar({
        ...workout,
        duration: workout.duration
      }, selectedSlot);

      // Schedule recurring workouts if enabled
      if (success && recurrence !== 'none') {
        const interval = recurrence === 'weekly' ? 1 : 2;
        for (let i = 1; i <= recurrenceWeeks; i++) {
          const recurringDate = addWeeks(selectedSlot.start, i * interval);
          const recurringSlot = {
            ...selectedSlot,
            start: recurringDate,
            end: addWeeks(selectedSlot.end, i * interval)
          };
          await addWorkoutToCalendar(workout, recurringSlot);
        }
      }

      setShowConfirmation(true);
      setTimeout(() => {
        onScheduled();
      }, 2000);
    } catch (error) {
      console.error('Error scheduling workout:', error);
      setIsScheduling(false);
    }
  };

  if (showSettings) {
    return <SchedulerSettings onClose={() => setShowSettings(false)} />;
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="theme-card w-full max-w-md relative flex flex-col max-h-[85vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {isRescheduling ? 'Reschedule Workout' : 'Workout Scheduler'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Settings className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Calendar */}
        <div className="flex-1 overflow-y-auto p-4">
          <WorkoutCalendar
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            availableSlots={availableSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
            existingWorkout={existingWorkout || undefined}
          />

          {/* Recurrence Options */}
          {!isRescheduling && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Repeat className="w-4 h-4" />
                <span>Repeat</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setRecurrence('none')}
                  className={`p-2 rounded-lg text-sm transition-colors ${
                    recurrence === 'none'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  None
                </button>
                <button
                  onClick={() => setRecurrence('weekly')}
                  className={`p-2 rounded-lg text-sm transition-colors ${
                    recurrence === 'weekly'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  Weekly
                </button>
                <button
                  onClick={() => setRecurrence('biweekly')}
                  className={`p-2 rounded-lg text-sm transition-colors ${
                    recurrence === 'biweekly'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  Bi-weekly
                </button>
              </div>

              {recurrence !== 'none' && (
                <div className="mt-2">
                  <label className="text-sm text-muted-foreground">Number of weeks:</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={recurrenceWeeks}
                    onChange={(e) => setRecurrenceWeeks(parseInt(e.target.value))}
                    className="w-full mt-1 px-3 py-1.5 bg-muted rounded-lg text-sm"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border shrink-0">
          <button
            onClick={handleSchedule}
            disabled={!selectedSlot || isScheduling}
            className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
              selectedSlot && !isScheduling
                ? 'theme-button'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isScheduling ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Clock className="w-5 h-5" />
                </motion.div>
                Scheduling...
              </>
            ) : showConfirmation ? (
              <>
                <Check className="w-5 h-5" />
                Scheduled!
              </>
            ) : (
              <>
                {isRescheduling ? 'Reschedule' : 'Schedule'} {recurrence !== 'none' && 'Recurring'} Workout
                {recurrence !== 'none' && ` (${recurrenceWeeks} weeks)`}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}